import { atom } from 'jotai';

export const FEATURE_DISCOVERY_STORAGE_PREFIX = 'pique_feature_discovery_';

export type FeatureId =
  | 'action-items'
  | 'start-focus'
  | 'followup-email'
  | 'pre-read'
  | 'ai-chat';

const FEATURE_IDS: FeatureId[] = [
  'action-items',
  'start-focus',
  'followup-email',
  'pre-read',
  'ai-chat',
];

function readSeenState(): Record<FeatureId, boolean> {
  const seen: Record<string, boolean> = {};
  for (const id of FEATURE_IDS) {
    seen[id] =
      localStorage.getItem(`${FEATURE_DISCOVERY_STORAGE_PREFIX}${id}`) ===
      'true';
  }
  return seen as Record<FeatureId, boolean>;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled;
}

const IS_CANARY = runtimeConfig.appBuildType === 'canary';

function buildInitialQueue(): FeatureId[] {
  const seen = readSeenState();
  const unseen = FEATURE_IDS.filter(id => !seen[id]);
  if (unseen.length === 0) return [];
  if (!IS_CANARY && Math.random() >= 0.7) return [];
  return shuffleArray(unseen);
}

const _featureDiscoverySeenAtom = atom<Record<FeatureId, boolean>>(
  readSeenState()
);

export const featureDiscoverySeenAtom = atom(
  get => get(_featureDiscoverySeenAtom),
  (_get, set, value: Record<FeatureId, boolean>) =>
    set(_featureDiscoverySeenAtom, value)
);

const _candidateQueueAtom = atom<FeatureId[]>(buildInitialQueue());

export const activeFeatureAtom = atom(get => {
  const queue = get(_candidateQueueAtom);
  return queue[0] ?? null;
});

export const skipFeatureAtom = atom(null, (get, set) => {
  const queue = get(_candidateQueueAtom);
  set(_candidateQueueAtom, queue.length <= 1 ? [] : queue.slice(1));
});

export const markFeatureSeenAtom = atom(
  null,
  (get, set, id: FeatureId) => {
    localStorage.setItem(`${FEATURE_DISCOVERY_STORAGE_PREFIX}${id}`, 'true');
    const newSeen = { ...get(featureDiscoverySeenAtom), [id]: true };
    set(featureDiscoverySeenAtom, newSeen);
    set(_candidateQueueAtom, get(_candidateQueueAtom).filter(fId => fId !== id));
  }
);

export const resetAllFeatureDiscoveryAtom = atom(null, (_get, set) => {
  for (const id of FEATURE_IDS) {
    localStorage.removeItem(`${FEATURE_DISCOVERY_STORAGE_PREFIX}${id}`);
  }
  set(featureDiscoverySeenAtom, readSeenState());
  set(_candidateQueueAtom, shuffleArray(FEATURE_IDS));
});

const VIDEO_BASE =
  'https://pique-static-assets.s3.us-west-2.amazonaws.com/static/feature-discovery';

const VIDEO_URLS: Record<FeatureId, string> = {
  'action-items': `${VIDEO_BASE}/action-items.mp4`,
  'start-focus': `${VIDEO_BASE}/start-focus.mp4`,
  'followup-email': `${VIDEO_BASE}/followup-email.mp4`,
  'pre-read': `${VIDEO_BASE}/pre-read.mp4`,
  'ai-chat': `${VIDEO_BASE}/ai-chat.mp4`,
};

const videoBlobCache = new Map<FeatureId, string>();
const videoReadySet = new Set<FeatureId>();
const videoReadyListeners = new Set<() => void>();

function notifyVideoReady() {
  videoReadyListeners.forEach(listener => listener());
}

for (const id of FEATURE_IDS) {
  fetch(VIDEO_URLS[id])
    .then(res => res.blob())
    .then(blob => {
      videoBlobCache.set(id, URL.createObjectURL(blob));
      videoReadySet.add(id);
      notifyVideoReady();
    })
    .catch(() => {});
}

export const videoReadyAtom = atom<Set<FeatureId>>(videoReadySet);

export function subscribeVideoReady(callback: () => void) {
  videoReadyListeners.add(callback);
  return () => { videoReadyListeners.delete(callback); };
}

export function getVideoBlobUrl(id: FeatureId): string | undefined {
  return videoBlobCache.get(id);
}

export function isVideoReady(id: FeatureId): boolean {
  return videoReadySet.has(id);
}
